import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin, ChevronLeft, ChevronRight, Play, Pause, Maximize, Minimize,
  MoreHorizontal, Flag, SlidersHorizontal, X, Info,
} from "lucide-react";
import { getRandom, getEligibleIds, reportPhoto, SLIDESHOW_OPTIONS } from "./api5";
import { NoMatch } from "./NoMatch";

const IDLE_MS = 3500;

export function Viewer({ prefs, countries, continents, reloadKey, onExit, onOpenPrefs, onWholeWorld, onPersistSpeed }) {
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [active, setActive] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [speed, setSpeed] = useState(prefs.slideshow_seconds || 10);
  useEffect(() => { setSpeed(prefs.slideshow_seconds || 10); }, [prefs.slideshow_seconds]);

  const idleTimer = useRef(null);
  const slideTimer = useRef(null);
  // Shuffle-without-replacement cycle: every eligible photo is shown once before any
  // repeats; when exhausted we reshuffle (avoiding an immediate repeat of the last photo).
  const cycleRef = useRef([]);
  const idxRef = useRef(0);
  const lastShownRef = useRef(null);
  // Session viewing history (item 3): every photograph actually displayed (manual OR
  // autoplay) is pushed here. `histPosRef` points at the currently-displayed entry.
  // Previous/Next walk this exact history; only advancing PAST the newest entry consumes
  // a fresh id from the shuffle-without-replacement cycle. Reset on filter change.
  const historyRef = useRef([]);
  const histPosRef = useRef(-1);

  // Hidden, opt-in TEST override (no UI, no persistence, read once from the URL).
  // Normal users have no query params and always receive the standard `desktop` variant
  // with the normal shuffle/history behaviour — unchanged.
  //   ?wwvariant=uhd  -> request the UHD (4K) variant instead of desktop (any other value ignored).
  //   ?photo_id=<id>  -> pin the FIRST displayed photograph to that id, so the exact same
  //                      photo can be loaded at desktop vs uhd for like-for-like comparison.
  const overrideRef = useRef(null);
  if (overrideRef.current === null) {
    const qs = new URLSearchParams(window.location.search);
    overrideRef.current = {
      variant: qs.get("wwvariant") === "uhd" ? "uhd" : "desktop",
      pinnedId: qs.get("photo_id") || null,
    };
  }

  // Item 7: while the full-screen Wallpaper Player is open, lock the underlying
  // page so no vertical/horizontal scrollbar or accidental page scrolling appears
  // behind the fixed overlay. Lock BOTH <html> and <body> overflow (no reserved
  // scrollbar gutter). Restored cleanly on exit (component unmount).
  useEffect(() => {
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, []);

  // Build a fresh shuffled cycle from the currently-eligible set (live query, so newly
  // published photos appear at the next reshuffle without any redeploy).
  const buildCycle = useCallback(async () => {
    const r = await getEligibleIds();
    let ids = r.ok && Array.isArray(r.data.ids) ? [...r.data.ids] : [];
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    // Don't start the new cycle with the same photo that ended the previous one.
    if (ids.length > 1 && lastShownRef.current && ids[0] === lastShownRef.current) {
      const k = 1 + Math.floor(Math.random() * (ids.length - 1));
      [ids[0], ids[k]] = [ids[k], ids[0]];
    }
    cycleRef.current = ids;
    idxRef.current = 0;
    return ids;
  }, []);

  const applyResp = useCallback(async (r) => {
    setLoading(false);
    if (r.status === 401) { toast.error("Please log in"); onExit(); return; }
    if (r.status === 403) { toast.error("Subscriber access needed to enter Random World."); onExit(); return; }
    if (r.ok && r.data.empty) { setEmpty(true); setPhoto(null); return; }
    if (r.ok) {
      setEmpty(false);
      const p = r.data.photo;
      lastShownRef.current = p.photo_id;
      // Decode the incoming image BEFORE swapping so caption + picture update together.
      // Item 8: determine orientation from the decoded image so the blurred-background
      // filler is applied ONLY to portrait photographs; landscape keeps the original
      // full-screen (cover) behaviour with no blurred side borders.
      try {
        const im = new window.Image();
        im.src = p.image_url;
        if (im.decode) await im.decode();
        setPortrait(im.naturalHeight > im.naturalWidth);
      } catch (_e) { setPortrait(false); /* show anyway if decode is unsupported/fails */ }
      setPhoto(p);
    }
  }, [onExit]);

  const fetchNext = useCallback(async () => {
    setLoading(true); setMenuOpen(false);
    const variant = overrideRef.current.variant;
    // Walk FORWARD through existing history first (e.g. after Previous), without consuming
    // the shuffle cycle, so back/forward navigation never counts as a new selection.
    if (histPosRef.current < historyRef.current.length - 1) {
      histPosRef.current += 1;
      await applyResp(await getRandom(variant, historyRef.current[histPosRef.current]));
      return;
    }
    // At the newest history item — pull the next id from the shuffle-without-replacement cycle.
    let cycle = cycleRef.current;
    if (!cycle.length || idxRef.current >= cycle.length) {
      cycle = await buildCycle();
    }
    if (!cycle.length) {
      // No eligible photos — ask the server so we get the correct empty/no-match reason.
      await applyResp(await getRandom(variant));
      return;
    }
    const id = cycle[idxRef.current];
    idxRef.current += 1;
    historyRef.current.push(id);
    histPosRef.current = historyRef.current.length - 1;
    await applyResp(await getRandom(variant, id));
  }, [buildCycle, applyResp]);

  // Previous walks BACKWARDS through the exact viewing history (no shuffle consumption).
  const fetchPrev = useCallback(async () => {
    if (histPosRef.current <= 0) return; // already at the oldest photograph in this session
    setLoading(true); setMenuOpen(false);
    histPosRef.current -= 1;
    await applyResp(await getRandom(overrideRef.current.variant, historyRef.current[histPosRef.current]));
  }, [applyResp]);

  // Rebuild the cycle ONLY when filters change (reloadKey) or on first mount — never on
  // unrelated parent re-renders. A ref holds the latest fetchNext so this effect need not
  // depend on its identity (which would otherwise reset the cycle mid-stream).
  const fetchNextRef = useRef(fetchNext);
  useEffect(() => { fetchNextRef.current = fetchNext; }, [fetchNext]);
  useEffect(() => {
    cycleRef.current = []; idxRef.current = 0; lastShownRef.current = null;
    historyRef.current = []; histPosRef.current = -1;
    const pinned = overrideRef.current.pinnedId;
    if (pinned) {
      // Test override: seed history with the pinned photo so the FIRST frame is exactly
      // that photograph at the chosen variant (like-for-like desktop vs uhd). Next/Prev
      // then behave normally (Next pulls from the shuffle cycle beyond this seed).
      setLoading(true); setMenuOpen(false);
      historyRef.current = [pinned]; histPosRef.current = 0;
      (async () => { await applyResp(await getRandom(overrideRef.current.variant, pinned)); })();
    } else {
      fetchNextRef.current();
    }
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean slideshow: manual advance restarts the timer from the new photograph.
  const armSlideshow = useCallback(() => {
    if (slideTimer.current) clearTimeout(slideTimer.current);
    if (playing && photo) slideTimer.current = setTimeout(() => fetchNext(), speed * 1000);
  }, [playing, photo, speed, fetchNext]);
  useEffect(() => { armSlideshow(); return () => slideTimer.current && clearTimeout(slideTimer.current); }, [armSlideshow]);

  const wake = useCallback(() => {
    setActive(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setActive(false), IDLE_MS);
  }, []);
  useEffect(() => { wake(); return () => idleTimer.current && clearTimeout(idleTimer.current); }, [wake]);

  const next = () => { if (slideTimer.current) clearTimeout(slideTimer.current); fetchNext(); wake(); };
  const prev = () => { if (slideTimer.current) clearTimeout(slideTimer.current); fetchPrev(); wake(); };
  const togglePlay = () => { setPlaying((p) => !p); wake(); };

  const changeSpeed = (v) => { const s = Number(v); setSpeed(s); onPersistSpeed(s); };

  const toggleFs = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.().then(() => setIsFs(true)).catch(() => {});
    else document.exitFullscreen?.().then(() => setIsFs(false)).catch(() => {});
  };
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      wake();
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " ") { e.preventDefault(); togglePlay(); }
      else if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const doReport = async () => {
    if (!photo) return;
    const r = await reportPhoto(photo.photo_id);
    r.ok ? toast.success("Thank you — sent to our moderators.") : toast.error("Report failed");
    setMenuOpen(false);
  };

  const loc = photo && [photo.location.place, photo.location.city, photo.location.country].filter(Boolean).join(", ");
  const showControls = active;
  const alwaysDetails = !!prefs.always_show_details;

  return (
    <div className="ww-viewer" data-testid="viewer-overlay"
      onMouseMove={wake} onTouchStart={wake} onClick={wake}
      style={{ cursor: showControls ? "default" : "none" }}>

      <AnimatePresence>
        {photo && (
          <motion.div key={photo.photo_id} className={`ww-viewer-frame ${portrait ? "is-portrait" : "is-landscape"}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            {portrait && <img src={photo.image_url} alt="" aria-hidden="true" className="ww-viewer-bg" />}
            <img src={photo.image_url} alt={loc || "photograph"} className="ww-viewer-img" data-testid="viewer-image" />
          </motion.div>
        )}
      </AnimatePresence>

      {loading && !photo && <div className="ww-viewer-loading" data-testid="viewer-loading">Finding a moment worth seeing…</div>}

      {/* Item 6 — when the user prefers it, the details legend stays permanently
          visible (its own overlay background keeps it readable over any photo). */}
      {photo && alwaysDetails && (
        <div className="ww-meta-pill ww-meta-always" data-testid="viewer-meta">
          <MapPin size={18} style={{ marginTop: 2 }} />
          <div>
            <div className="ww-meta-loc">{loc}</div>
            <div className="ww-meta-sub">{photo.date_display} · Shared by {photo.contributor_handle}</div>
          </div>
        </div>
      )}

      {empty && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "var(--ww-viewer)" }}>
          <NoMatch dark onChange={onOpenPrefs} onWholeWorld={onWholeWorld} />
        </div>
      )}

      <AnimatePresence>
        {photo && showControls && (
          <motion.div key="chrome" className="ww-viewer-chrome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <div className="ww-viewer-scrim-top" />
            <div className="ww-viewer-scrim-bottom" />

            {!alwaysDetails && (
              <div className="ww-meta-pill" data-testid="viewer-meta">
                <MapPin size={18} style={{ marginTop: 2 }} />
                <div>
                  <div className="ww-meta-loc">{loc}</div>
                  <div className="ww-meta-sub">{photo.date_display} · Shared by {photo.contributor_handle}</div>
                </div>
              </div>
            )}

            <div className="ww-top-actions">
              <button className="ww-round" data-testid="viewer-more" aria-label="More" onClick={() => setMenuOpen((m) => !m)}>
                <MoreHorizontal size={18} />
              </button>
              <button className="ww-round" data-testid="viewer-close" aria-label="Close viewer" onClick={onExit}><X size={18} /></button>
              {menuOpen && (
                <div style={{ position: "absolute", top: 50, right: 0, background: "rgba(20,16,12,0.92)", border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 10, padding: 6, minWidth: 160 }} data-testid="viewer-menu">
                  <button className="ww-chip-dark" style={{ width: "100%", justifyContent: "flex-start", background: "transparent", border: "none" }}
                    data-testid="viewer-report" onClick={doReport}><Flag size={15} /> Report photograph</button>
                </div>
              )}
            </div>

            <button className="ww-round ww-side-nav left" aria-label="Previous photograph" onClick={prev} data-testid="viewer-prev"><ChevronLeft size={22} /></button>
            <button className="ww-round ww-side-nav right" aria-label="Next photograph" onClick={next} data-testid="viewer-next-side"><ChevronRight size={22} /></button>

            <div className="ww-bottom-left">
              <button className="ww-chip-dark" data-testid="viewer-where-when" onClick={onOpenPrefs}>
                <SlidersHorizontal size={15} /> Where &amp; when
              </button>
            </div>

            <div className="ww-bottom-bar">
              <div className="ww-transport">
                <button className="ww-round" aria-label="Previous photograph" onClick={prev} data-testid="viewer-skip-back"><ChevronLeft size={20} /></button>
                <button className="ww-play" data-testid="viewer-playpause" aria-label={playing ? "Pause slideshow" : "Play slideshow"} onClick={togglePlay}>
                  {playing ? <Pause size={24} /> : <Play size={24} />}
                </button>
                <button className="ww-round" aria-label="Next" onClick={next} data-testid="viewer-next"><ChevronRight size={20} /></button>
              </div>
            </div>

            <div className="ww-bottom-right">
              <div className="ww-chip-dark" style={{ gap: 6 }}>
                <span style={{ opacity: .8 }}>Interval</span>
                <select className="ww-interval-select" value={speed} onChange={(e) => changeSpeed(e.target.value)} data-testid="viewer-interval" aria-label="Slideshow interval">
                  {SLIDESHOW_OPTIONS.map((s) => <option key={s} value={s}>{`${s} sec`}</option>)}
                </select>
              </div>
              <button className="ww-round" aria-label={isFs ? "Exit full screen" : "Full screen"} onClick={toggleFs} data-testid="viewer-fullscreen">
                {isFs ? <Minimize size={18} /> : <Maximize size={18} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
